import React, {useRef, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  checkOrderConfirmation,
  setScanPackPromptInput,
  submitServiceIssue,
  closePrompt,
  submitNoteFromPacker,
  submitServiceIssueNote,
  submitRestartOrder,
  submitClickScanConfirmation,
  setScanPackPrompt,
  submitTypeScan,
  submitTypeScanConfirmation,
  submitSerialScan,
  submitSerialScanSecond,
  submitProductInstructionsConfirmation,
  closeProductInstructionsPrompt,
  submitAddItem,
  submitKitConfirmation,
  closeKitConfirmation,
  submitWmsLotVerification,
  submitWmsZeroEscalation,
  submitWmsZeroReceiveLot,
  submitWmsZeroReceiveQty,
  submitWmsZeroBypass,
  cancelWmsZeroPrompt
} from '../../actions/scanpackScanningActions';
import OrderConfirmationPopUp from '../scanpack/orderConfirmationPopUp';
import WmsLotVerificationPrompt from './WmsLotVerificationPrompt';
import WmsZeroInventoryPrompt from './WmsZeroInventoryPrompt';
import Alert from '../scanpack/alert';
import Prompt from '../../commons/prompt';
import ClickScanConfirmationPopUp from '../scanpack/click_scan_confirmation_pop_up';
import TypeScanAlert from '../scanpack/type_scan_alert';
import SerialRecord from '../scanpack/serial_record';
import PopUpModel from '../scanpack/pop_up_model';
import ProductConfirmationCheck from '../scanpack/productConfirmationCheck';
import i18n from 'i18n-js';
import useWidth from '../scanToCartScanning/useWidth';

const ScanPackPrompts = () => {
  const dispatch = useDispatch();
  const serialFocus = useRef(null);
  const windowWidth = useWidth();

  const currentOrder = useSelector(state => state.scanPackScanning.currentOrder);
  const bothSettings = useSelector(state => state.user.bothSettings);
  const prompt = useSelector(state => state.scanPackScanning.prompt);
  const loading = useSelector(state => state.scanPackScanning.loading);
  const wmsZeroPending = useSelector(state => state.scanPackScanning.wmsZeroPending);
  // The gated-scan thunks flip this for the duration of the server round trip; the WMS prompts
  // use it to refuse a second submit for the same answer. It is set and cleared by the thunk,
  // so it cannot stick on failure.
  const wmsSubmitInFlight = useSelector(state => state.scanPackScanning.loading);

  const unScannedItems = currentOrder?.unscanned_items || [];
  const scanPackSetting = bothSettings?.data?.data?.scanpack_setting;
  const generalSetting = bothSettings?.data?.data?.general_setting;

  useEffect(() => {
    if (prompt.isVisible && prompt.type === 'alert') {
      const timer = setTimeout(() => {
        dispatch(closePrompt());
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [prompt.isVisible, prompt.type, dispatch]);

  useEffect(() => {
    if (prompt.isVisible && prompt.error && prompt.type !== 'alert') {
      const timer = setTimeout(() => {
        dispatch(setScanPackPrompt(true, prompt.message, prompt.type, '', false));
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [prompt.error, prompt.type, prompt.message, dispatch]);

  const handleClose = () => {
    dispatch(closePrompt());
  };

  const handleInputChange = (text) => {
    dispatch(setScanPackPromptInput(text));
  };

  const promptRenderers = {
    product_instructions_confirmation: () => {
      const currentItem = unScannedItems[0]?.child_items?.[0] || unScannedItems[0];
      return (
        <>
          {prompt.error && (
            <PopUpModel
              closeAlert={() => dispatch(setScanPackPrompt(true, '', 'product_instructions_confirmation', '', false))}
              message={i18n.t('productConfirmationPopup.productConfirmationAlert')}
              messageTypeSuccess={false}
            />
          )}
          <ProductConfirmationCheck
            ProductConfirmationCheckFun={() => dispatch(submitProductInstructionsConfirmation())}
            closeProductConfirmationCheckPopUp={() => dispatch(closeProductInstructionsPrompt())}
            order={currentOrder}
            kitInstructions={currentItem?.instruction || ''}
            productInstructions={currentItem?.instruction || ''}
            ProductConfirmationCheckInput={prompt.input}
            handleProductConfirmationCheckInput={handleInputChange}
          />
        </>
      );
    },

    type_scan_confirmation: () => {
      const currentItem = unScannedItems[0]?.child_items?.[0] || unScannedItems[0];
      return (
        <>
          {prompt.error && (
            <PopUpModel
              closeAlert={() => dispatch(setScanPackPrompt(true, '', 'type_scan_confirmation', '', false))}
              message={i18n.t('productConfirmationPopup.productConfirmationAlert')}
              messageTypeSuccess={false}
            />
          )}
          <ProductConfirmationCheck
            ProductConfirmationCheckFun={() => dispatch(submitTypeScanConfirmation())}
            closeProductConfirmationCheckPopUp={handleClose}
            order={currentOrder}
            kitInstructions={currentItem?.instruction || ''}
            productInstructions={currentItem?.instruction || ''}
            ProductConfirmationCheckInput={prompt.input}
            handleProductConfirmationCheckInput={handleInputChange}
          />
        </>
      );
    },

    kit_confirmation: () => (
      <>
        {prompt.error && (
          <PopUpModel
            closeAlert={() => dispatch(setScanPackPrompt(true, prompt.message, 'kit_confirmation', '', false))}
            message={i18n.t('productConfirmationPopup.productConfirmationAlert')}
            messageTypeSuccess={false}
          />
        )}
        <ProductConfirmationCheck
          ProductConfirmationCheckFun={() => dispatch(submitKitConfirmation())}
          closeProductConfirmationCheckPopUp={() => dispatch(closeKitConfirmation())}
          order={currentOrder}
          kitInstructions={prompt.message}
          productInstructions={''}
          ProductConfirmationCheckInput={prompt.input}
          handleProductConfirmationCheckInput={handleInputChange}
        />
      </>
    ),

    restart: () => (
      <Prompt
        onPressCancel={handleClose}
        onPressOk={() => dispatch(submitRestartOrder())}
        msg={i18n.t('scanpackItem.restartAlert')}
      />
    ),

    note_from_packer: () => (
      <Alert
        closeAlert={handleClose}
        submitAlert={() => dispatch(submitNoteFromPacker())}
        notes_fromPacker={prompt.input}
        noteUpdate={handleInputChange}
        scanPackSetting={scanPackSetting}
        generalSetting={generalSetting}
        msg={prompt.message}
        showIcon={true}
        showButtons={true}
      />
    ),

    service_issue_note: () => (
      <Alert
        closeAlert={handleClose}
        submitAlert={() => dispatch(submitServiceIssueNote())}
        notes_fromPacker={prompt.input}
        noteUpdate={handleInputChange}
        scanPackSetting={scanPackSetting}
        generalSetting={generalSetting}
        msg={prompt.message}
        showIcon={true}
        showButtons={true}
      />
    ),

    add_item: () => (
      <>
        {prompt.error && (
          <PopUpModel
            closeAlert={() => dispatch(setScanPackPrompt(true, prompt.message, 'add_item', '', false))}
            message={prompt.error}
            messageTypeSuccess={false}
          />
        )}
        <Alert
          closeAlert={handleClose}
          submitAlert={() => dispatch(submitAddItem())}
          notes_fromPacker={prompt.input}
          noteUpdate={handleInputChange}
          scanPackSetting={scanPackSetting}
          generalSetting={generalSetting}
          msg={prompt.message}
          showIcon={true}
          showButtons={false}
          loading={loading}
          placeholder={'Ready for product Scan'}
          placeholderTextColor={'#acacad'}
        />
      </>
    ),

    order_confirmation: () => {
      if (unScannedItems.length === 0) return null;
      return (
        <OrderConfirmationPopUp
          OrderConfirmationCheckFun={() => dispatch(checkOrderConfirmation())}
          OrderConfirmationClosePopup={handleClose}
          OrderConfirmationCheckInput={prompt.input}
          errorMessageOrderConfirmationCheck={prompt.error}
          handleOrderConfirmationCheckInput={handleInputChange}
          datcheckHere={handleClose}
          orderDetails={currentOrder}
          popupShow={prompt.isVisible}
          bothSettings={bothSettings}
        />
      );
    },

    service_issue: () => (
      <Alert
        closeAlert={handleClose}
        submitAlert={() => dispatch(submitServiceIssue())}
        notes_fromPacker={prompt.input}
        noteUpdate={handleInputChange}
        scanPackSetting={scanPackSetting}
        generalSetting={generalSetting}
        msg={prompt.message}
        heading={'scanpackItem.service_Issue_msg'}
        showIcon={false}
        showButtons={false}
      />
    ),

    click_scan_confirmation: () => (
      <ClickScanConfirmationPopUp
        clickScanConfirmFun={() => dispatch(submitClickScanConfirmation())}
        clickScanConfirmInput={prompt.input}
        errorMessageClickScanConfirmation={prompt.error}
        handleClickScanConfirmInput={handleInputChange}
        closeAlert={handleClose}
        closeErrorAlert={() => dispatch(setScanPackPrompt(true, '', 'click_scan_confirmation', '', false))}
        serialFocus={serialFocus}
        windowWidth={windowWidth}
      />
    ),

    alert: () => (
      <PopUpModel
        closeAlert={handleClose}
        message={prompt.message}
        messageTypeSuccess={false}
      />
    ),

    type_scan: () => (
      <TypeScanAlert
        closeAlert={handleClose}
        typeScanCount={prompt.input}
        typeScanCountUpdate={handleInputChange}
        submitTypeScan={() => dispatch(submitTypeScan())}
        scanPackSetting={scanPackSetting}
        generalSetting={generalSetting}
      />
    ),

    serial_scan: () => {
      const isSecond = prompt.type === 'serial_scan_second';
      return (
        <>
          {prompt.error && (
            <PopUpModel
              closeAlert={() => dispatch(setScanPackPrompt(true, prompt.message, prompt.type, '', false))}
              message={typeof prompt.error === 'string' ? prompt.error : i18n.t('scanpackItem.Scanpack_required_serial_msg')}
              messageTypeSuccess={false}
            />
          )}
          <SerialRecord
            serialRecordFun={isSecond ? () => dispatch(submitSerialScanSecond()) : () => dispatch(submitSerialScan())}
            serialRecordText={prompt.message}
            serialRecordInput={prompt.input}
            handleSerialRecordInput={handleInputChange}
            closeSerialRecord={handleClose}
          />
        </>
      );
    }
  };

  promptRenderers.serial_scan_second = promptRenderers.serial_scan;

  promptRenderers.wms_lot_verification = () => (
    <WmsLotVerificationPrompt
      message={prompt.message}
      input={prompt.input}
      onChangeInput={handleInputChange}
      onSubmit={() => dispatch(submitWmsLotVerification())}
      onCancel={handleClose}
      error={prompt.error}
      isSubmitting={wmsSubmitInFlight}
    />
  );

  promptRenderers.wms_zero_escalation = () => (
    <WmsZeroInventoryPrompt
      testID="wmsZeroEscalationPrompt"
      message={prompt.message}
      input={prompt.input}
      onChangeInput={handleInputChange}
      onSubmit={() => dispatch(submitWmsZeroEscalation())}
      onCancel={() => dispatch(cancelWmsZeroPrompt())}
      error={prompt.error}
      canBypass={!!wmsZeroPending?.canBypass}
      onBypass={() => dispatch(submitWmsZeroBypass())}
      isSubmitting={wmsSubmitInFlight}
    />
  );

  promptRenderers.wms_zero_receive_lot = () => {
    const isQtyStep = prompt.type === 'wms_zero_receive_qty';
    return (
      <WmsZeroInventoryPrompt
        testID={isQtyStep ? 'wmsZeroReceiveQtyPrompt' : 'wmsZeroReceiveLotPrompt'}
        message={prompt.message}
        input={prompt.input}
        onChangeInput={handleInputChange}
        onSubmit={() => dispatch(isQtyStep ? submitWmsZeroReceiveQty() : submitWmsZeroReceiveLot())}
        onCancel={() => dispatch(cancelWmsZeroPrompt())}
        error={prompt.error}
        canBypass={!!wmsZeroPending?.canBypass}
        onBypass={() => dispatch(submitWmsZeroBypass())}
        isSubmitting={wmsSubmitInFlight}
      />
    );
  };

  promptRenderers.wms_zero_receive_qty = promptRenderers.wms_zero_receive_lot;

  if (!prompt.isVisible) return null;

  const renderer = promptRenderers[prompt.type];
  return renderer ? renderer() : null;
};

export default ScanPackPrompts;
